<script lang="ts">
  import { onMount } from 'svelte';
  import { advanceWave } from './wave-motion';

  export let readLevel: () => number = () => 0;
  export let active = false;
  export let speaking = false;
  export let compact = false;
  let canvas: HTMLCanvasElement;
  let wake = () => {};
  $: { active; readLevel; speaking; compact; wake(); }

  onMount(() => {
    const context = canvas.getContext('2d');
    if (!context) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let lastTime = 0;
    let phase = 0;
    let amplitude = 0;
    let visible = true;
    let disposed = false;
    let width = 300;
    let height = 150;
    let colour = '#7123c0';

    function draw() {
      if (!context) return;
      context.clearRect(0, 0, width, height);
      const gradient = context.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, 'transparent');
      gradient.addColorStop(0.22, colour);
      gradient.addColorStop(0.78, colour);
      gradient.addColorStop(1, 'transparent');
      context.strokeStyle = gradient;
      context.lineWidth = 2;
      context.lineCap = 'round';
      for (let layer = 0; layer < 2; layer++) {
        context.globalAlpha = layer === 0 ? 1 : 0.2;
        context.beginPath();
        for (let i = 0; i <= 160; i++) {
          const x = i / 160;
          const envelope = Math.exp(-Math.pow((x - 0.5) * 5, 2)) * Math.sin(Math.PI * x);
          const y = height / 2 + Math.sin(x * Math.PI * 10 - phase + layer * 0.5)
            * envelope * amplitude * height * 0.42 * (layer ? 0.65 : 1);
          if (i === 0) context.moveTo(x * width, y);
          else context.lineTo(x * width, y);
        }
        context.stroke();
      }
      context.globalAlpha = 1;
    }

    function animate(time: number) {
      frame = 0;
      if (disposed || !visible || document.hidden) return;
      const elapsed = lastTime ? Math.min(64, time - lastTime) : 16;
      lastTime = time;
      if (reducedMotion.matches) {
        amplitude = 0;
      } else {
        amplitude = advanceWave(amplitude, active ? readLevel() : 0, active, speaking, elapsed);
        // Continuous phase: incoming volume changes never restart the wave.
        phase = (phase + elapsed * 0.003) % (Math.PI * 2);
      }
      draw();
      if (!reducedMotion.matches && (active || amplitude > 0.001))
        frame = requestAnimationFrame(animate);
    }
    wake = () => {
      if (!disposed && !frame && visible && !document.hidden)
        frame = requestAnimationFrame(animate);
    };
    const resize = new ResizeObserver(() => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width || 300;
      height = rect.height || 150;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      colour = getComputedStyle(canvas).color;
      draw();
      wake();
    });
    const visibility = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
      wake();
    };
    const intersection = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      visibility();
    });
    resize.observe(canvas);
    intersection.observe(canvas);
    document.addEventListener('visibilitychange', visibility);
    reducedMotion.addEventListener('change', visibility);
    wake();
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resize.disconnect();
      intersection.disconnect();
      document.removeEventListener('visibilitychange', visibility);
      reducedMotion.removeEventListener('change', visibility);
      wake = () => {};
    };
  });
</script>

<canvas bind:this={canvas} class:compact aria-hidden="true"></canvas>

<style>
  canvas { display: block; width: min(100%, 300px); height: 150px; flex-shrink: 0; color: var(--cs-bg-button-color, #7123c0); }
  canvas.compact { height: 60px; }
</style>
